import { computed } from "vue";
import { useNow } from "@vueuse/core";
import { DateTime } from "luxon";
import { SETUP_ITEM_COPY } from "@/copy/setupProgress";
import {
  type SetupItem,
  isPutAway,
  useSetupProgressQuery,
} from "@/services/setup_progress";

const SUGGESTION_COUNT = 3;
const ACTIONABLE = new Set(["available", "ask_manager"]);

// Registry order is already the suggested order; New items jump ahead of it.
function bySuggestionOrder(a: SetupItem, b: SetupItem): number {
  return Number(b.isNew) - Number(a.isNew);
}

export function useSetupChecklist() {
  const query = useSetupProgressQuery();

  const items = computed(() =>
    (query.data?.items ?? []).filter((item) => item.key in SETUP_ITEM_COPY),
  );
  const core = computed(() => items.value.filter((i) => i.tier === "core"));
  const advanced = computed(() =>
    items.value.filter((i) => i.tier === "advanced"),
  );
  const tips = computed(() => items.value.filter((i) => i.tier === "tip"));

  const coreComplete = computed(
    () =>
      core.value.length > 0 &&
      // Nothing left the user can do now: what waits on a channel, data or a
      // manager doesn't keep advanced items from being suggested.
      core.value.every((i) => i.state !== "available"),
  );

  const canDismissAdvanced = computed(
    () =>
      !!query.data &&
      query.data.level.advancedDone >= query.data.level.advancedNeeded,
  );
  function putAway(item: SetupItem): boolean {
    return !!query.data && isPutAway(item, query.data.level);
  }

  const openAdvanced = computed(() =>
    advanced.value.filter((i) => ACTIONABLE.has(i.state) && !putAway(i)),
  );
  const openTips = computed(() =>
    tips.value.filter((i) => i.state === "available" && !i.dismissed),
  );

  // Tips are never suggested: they don't count toward a level and have a
  // place of their own.
  const suggestions = computed(() => {
    const pool = coreComplete.value
      ? openAdvanced.value
      : core.value.filter((i) => ACTIONABLE.has(i.state));
    return [...pool].sort(bySuggestionOrder).slice(0, SUGGESTION_COUNT);
  });

  // Not before the essentials are done: setting up matters more then. `now`
  // ticks so a Home left open overnight moves on.
  const now = useNow({ interval: 60_000 });
  const todaysTip = computed(() => {
    if (!coreComplete.value) return;
    const pool = openTips.value;
    if (pool.length === 0) return;
    const today = DateTime.fromJSDate(now.value);
    return pool[(today.year * 366 + today.ordinal) % pool.length];
  });

  // Waiting on a channel or data comes first, since the user can act once it
  // arrives; a manager only once nothing open is left.
  const upNext = computed(
    () =>
      core.value.find((i) => i.state === "locked") ??
      (coreComplete.value
        ? core.value.find((i) => i.state === "ask_manager")
        : undefined),
  );

  const isNewOrganization = computed(
    () =>
      core.value.some(
        (i) => i.key === "connect_channels" && i.state !== "done",
      ) &&
      core.value.some(
        (i) => i.state === "locked" && i.lockedReason === "needs_resources",
      ),
  );

  return {
    query,
    items,
    core,
    advanced,
    tips,
    suggestions,
    upNext,
    todaysTip,
    canDismissAdvanced,
    putAway,
    isNewOrganization,
    level: computed(() => query.data?.level),
    summary: computed(() => query.data?.summary),
  };
}
